import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  CatalogPublicationEditorWizard,
  toast,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  type PublicationEditorViewModel,
  type PublicationEditorFormValue,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const PUBLICATION_SORT_FIELDS = ['commission', 'created_at', 'pricebook', 'scope', 'status', 'storefront_key', 'title', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  CommerceBreadcrumbHeader,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
} from '@/views/commerce/catalogHierarchyShared';
import { buildPublicationBundleWarnings } from '@/views/commerce/publicationBundleWarnings';

import {
  canonicalizePublicationScope,
  createPublication,
  deletePublication,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listCatalogs,
  listCatalogProducts,
  listPricebooks,
  listPublications,
  listCommissionPlans,
  updatePublication,
  type Catalog,
  type CatalogPublicationSection,
  type CatalogPublication,
  type Pricebook,
  type CommissionPlan,
  type Product,
  type ProductSelector,
  type ProductSelectorRule,
} from '@/lib/commerce';

function selectorToForm(selector?: ProductSelector): PublicationEditorFormValue['productSelection'] {
  return {
    mode: selector?.mode === 'explicit_products' || selector?.mode === 'explicit_variants' || selector?.mode === 'rule_based'
      ? selector.mode
      : 'all_products',
    includedProductIds: selector?.included_product_ids || [],
    includedVariantIds: selector?.included_variant_ids || [],
    rules: (selector?.rules || []).map((rule, index) => ({
      ruleId: rule.rule_id || `rule-${index}`,
      attribute: rule.attribute,
      selectorKey: rule.selector_key || '',
      operator: rule.operator,
      valueLabels: rule.values || [],
      effectSummary: rule.effect_summary || '',
    })),
  };
}

function sectionToForm(section: CatalogPublicationSection): PublicationEditorFormValue['sections'][number] {
  return {
    sectionKey: section.section_key,
    name: section.name,
    description: section.description || '',
    status: section.status,
    displayMode: section.display_mode,
    selectionMode: section.selection_mode,
    sortOrder: section.sort_order,
    productSelection: selectorToForm(section.product_selector),
    suggestedPlacements: (section.placements || [])
      .filter(placement => placement.placement_kind === 'suggested')
      .sort((left, right) => left.sort_order - right.sort_order)
      .flatMap(placement => placement.variant_id
        ? [{ targetKind: 'variant' as const, targetId: placement.variant_id }]
        : placement.product_id
          ? [{ targetKind: 'product' as const, targetId: placement.product_id }]
          : []),
  };
}

function selectorToPayload(selector: PublicationEditorFormValue['productSelection']): ProductSelector {
  return {
    mode: selector.mode,
    included_product_ids: selector.includedProductIds,
    included_variant_ids: selector.includedVariantIds,
    rules: selector.rules.map((rule): ProductSelectorRule => ({
      rule_id: rule.ruleId,
      attribute: rule.attribute,
      selector_key: rule.selectorKey?.trim() || null,
      operator: rule.operator,
      values: rule.valueLabels.map(value => value.trim()).filter(Boolean),
      effect_summary: rule.effectSummary || null,
    })),
  };
}

function publicationScopeForEditor(scope: string | null | undefined): string {
  return canonicalizePublicationScope(scope) === 'public' ? 'public' : 'owner_only';
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'published':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

export default function PublicationsPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [publications, setPublications] = useState<CatalogPublication[]>([]);
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<Record<string, Product[]>>({});
  const [pricebooks, setPricebooks] = useState<Pricebook[]>([]);
  const [commissionPlans, setCommissionPlans] = useState<CommissionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['scope', 'status'],
    facetValues: {
      scope: ['owner_only', 'public', 'shared_tenants'],
      status: ['active', 'draft', 'published'],
    },
    sortFields: PUBLICATION_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalPublications, setTotalPublications] = useState(0);
  const [facetCounts, setFacetCounts] = useState<Record<string, Record<string, number>>>({});
  const requestSequence = useRef(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => [
    ...(query.facets.scope.length > 0 ? [{ id: 'scope', value: query.facets.scope }] : []),
    ...(query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : []),
  ], [query.facets.scope, query.facets.status]);

  const [form, setForm] = useState<PublicationEditorFormValue>({
    storefrontKey: '',
    catalogId: '',
    scope: 'public',
    status: 'draft',
    defaultPricebookId: null,
    commissionPlanId: null,
    productSelection: {
      mode: 'all_products',
      includedProductIds: [],
      includedVariantIds: [],
      rules: []
    },
    sections: [],
    metadata: []
  });

  const loadData = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) return;
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const [nextPublications, nextCatalogs, nextPricebooks, nextCommissionPlans] = await Promise.all([
        listPublications({
          catalogOwnerTenantId: activeTenant.id,
          search: searchQuery || undefined,
          scope: query.facets.scope,
          status: query.facets.status,
          sort: query.sort,
          include_facets: ['scope', 'status'],
          ...toLimitOffset(query.pagination),
        }),
        listCatalogs({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listPricebooks({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listCommissionPlans({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
      ]);
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextPublications.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      const nextCatalogProducts = Object.fromEntries(
        await Promise.all(
          (nextCatalogs || []).map(async catalog => {
            const response = await listCatalogProducts(catalog.catalog_id, {
              include_variants: true,
              include_bundle_components: true,
            });
            return [catalog.catalog_id, response.items] as const;
          }),
        ),
      );
      setPublications(nextPublications.items);
      setTotalPublications(nextPublications.total_count);
      setFacetCounts(Object.fromEntries(Object.entries(nextPublications.facets).map(([key, buckets]) => [
        key,
        Object.fromEntries(buckets.map(bucket => [bucket.value, bucket.count])),
      ])));
      setCatalogs(nextCatalogs || []);
      setCatalogProducts(nextCatalogProducts);
      setPricebooks(nextPricebooks || []);
      setCommissionPlans(nextCommissionPlans || []);
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load publication registry.'));
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [activeTenant?.id, query.facets.scope, query.facets.status, query.pagination, query.sort, searchQuery]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
  };

  const handleRowAction = useCallback((id: string, actionId: string) => {
    const pub = publications.find(p => p.publication_id === id);
    if (!pub) return;

    if (actionId === 'edit') {
      setEditingId(id);
      setForm({
        storefrontKey: pub.storefront_key || '',
        catalogId: pub.catalog_id,
        scope: publicationScopeForEditor(pub.scope),
        status: pub.status,
        defaultPricebookId: pub.pricebook_id || null,
        commissionPlanId: pub.commission_plan_id || null,
        productSelection: selectorToForm(pub.product_selector),
        sections: (pub.sections || []).map(sectionToForm),
        metadata: Object.entries(pub.metadata || {}).map(([key, val]) => ({
          id: key,
          key,
          value: String(val),
          valueType: 'string'
        }))
      });
      setEditorOpen(true);
    }
  }, [publications]);

  const deletePublicationRecord = useCallback(async (publication: CatalogPublication) => {
    if (!window.confirm(`Delete publication "${publication.storefront_key || 'Global'} / ${publication.catalog_key || publication.catalog_id}"?`)) {
      return;
    }

    setError(null);
    try {
      await deletePublication(publication.publication_id);
      await loadData();
      toast.success('Publication deleted', {
        description: publication.storefront_key || publication.catalog_key || publication.catalog_id,
      });
      if (editingId === publication.publication_id) {
        closeEditor();
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const msg = getCommerceErrorMessage(err, 'Failed to delete publication.');
        setError(msg);
        toast.error('Failed to delete publication', { description: msg });
      }
    }
  }, [editingId, loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        storefront_key: form.storefrontKey || null,
        catalog_id: form.catalogId,
        scope: canonicalizePublicationScope(form.scope),
        status: form.status,
        pricebook_id: form.defaultPricebookId === 'none' ? null : form.defaultPricebookId,
        commission_plan_id: form.commissionPlanId === 'none' ? null : form.commissionPlanId,
        product_selector: selectorToPayload(form.productSelection),
        sections: form.sections.map(section => ({
          section_key: section.sectionKey.trim(),
          name: section.name.trim(),
          description: section.description || null,
          status: section.status,
          display_mode: section.displayMode,
          selection_mode: section.selectionMode,
          sort_order: section.sortOrder,
          product_selector: selectorToPayload(section.productSelection),
          placements: section.suggestedPlacements.map((placement, sortOrder) => ({
            placement_kind: 'suggested',
            ...(placement.targetKind === 'product'
              ? { product_id: placement.targetId }
              : { variant_id: placement.targetId }),
            sort_order: sortOrder,
          })),
        })),
        metadata: form.metadata.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {})
      };

      if (editingId) {
        await updatePublication(editingId, payload);
        toast.success('Publication updated');
      } else {
        await createPublication(payload);
        toast.success('Publication created');
      }
      await loadData();
      closeEditor();
    } catch (err) {
      const msg = getCommerceErrorMessage(err, 'Failed to save publication.');
      setError(msg);
      toast.error('Failed to save publication', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'publications',
    searchQuery,
    searchPlaceholder: 'Search publications...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage catalog publications.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'create-publication',
      label: 'New Publication',
      onAction: () => {
        setEditingId(null);
        setForm({
          storefrontKey: '',
          catalogId: catalogs[0]?.catalog_id || '',
          scope: 'public',
          status: 'draft',
          defaultPricebookId: null,
          commissionPlanId: null,
          productSelection: { mode: 'all_products', includedProductIds: [], includedVariantIds: [], rules: [] },
          sections: [],
          metadata: []
        });
        setEditorOpen(true);
      }
    }
  });

  const columns = useMemo<ColumnDef<CatalogPublication>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'title',
      accessorFn: publication =>
        `${publication.storefront_key || 'Global'} ${publication.catalog_key || publication.catalog_id}`,
      size: 250,
      header: 'Storefront / Catalog',
      cell: ({ row }) => {
        const p = row.original;
        return (
          <button
            type="button"
            onClick={() => handleRowAction(p.publication_id, 'edit')}
            className="group min-w-0 text-left"
            aria-label={`Edit ${p.storefront_key || 'Global'} / ${p.catalog_key || p.catalog_id}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {p.storefront_key || 'Global'} / {p.catalog_key || p.catalog_id}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {[
                p.exposure_label,
                p.sections?.length ? `${p.sections.length} sections configured` : 'Publication selector controls the visible assortment.'
              ].filter(Boolean).join(' • ')}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'scope',
      size: 100,
      header: 'Scope',
      cell: ({ row }) => (
        <span className="rounded border border-border/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
          {row.original.scope}
        </span>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'pricebook',
      accessorKey: 'default_pricebook_name',
      size: 150,
      header: 'Default Pricebook',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.default_pricebook_name || 'None'}</span>
      ),
    },
    {
      id: 'commission',
      accessorKey: 'default_commission_plan_name',
      size: 150,
      header: 'Commission Plan',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.default_commission_plan_name || 'None'}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 120,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => handleRowAction(p.publication_id, 'edit')}
              aria-label={`Modify ${p.storefront_key || 'Global'} / ${p.catalog_key || p.catalog_id}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => void deletePublicationRecord(p)}
              aria-label={`Delete ${p.storefront_key || 'Global'} / ${p.catalog_key || p.catalog_id}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:text-destructive hover:bg-destructive/5"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [deletePublicationRecord, handleRowAction]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: PUBLICATION_SORT_FIELDS, registry: 'publications' });

  const selectedCatalogProducts = catalogProducts[form.catalogId] || [];
  const productOptions = selectedCatalogProducts.map(product => ({
    value: product.product_id,
    label: product.name,
    description: product.product_key,
  }));
  const variantOptions = selectedCatalogProducts.flatMap(product =>
    (product.variants || []).map(variant => ({
      value: variant.variant_id,
      label: `${product.name} / ${variant.name}`,
      description: variant.variant_key,
    })),
  );
  const bundleWarnings = buildPublicationBundleWarnings({
    products: selectedCatalogProducts,
    selection: form.productSelection,
  });

  const editorViewModel: PublicationEditorViewModel = {
    mode: editingId ? 'edit' : 'create',
    state: saving ? 'saving' : 'ready',
    isOpen: editorOpen,
    title: editingId ? 'Edit Publication' : 'New Publication',
    subtitle: 'Define one set of options. Choose where it is used in Storefront publication bindings.',
    saveActionLabel: editingId ? 'Update Publication' : 'Create Publication',
    value: form,
    catalogOptions: catalogs.map(c => ({ value: c.catalog_id, label: c.name, description: c.catalog_key })),
    pricebookOptions: [
      { value: 'none', label: 'No Pricebook' },
      ...pricebooks.map(pb => ({
        value: pb.pricebook_id,
        label: pb.name,
        description: pb.currency || undefined,
        disabled: pb.status !== 'active',
        metadata: { catalogId: pb.catalog_id || '' },
      }))
    ],
    commissionPlanOptions: [
      { value: 'none', label: 'No Commission Plan' },
      ...commissionPlans.map(cp => ({ value: cp.commission_plan_id, label: cp.name }))
    ],
    scopeOptions: [
      { value: 'public', label: 'Public Storefront', description: 'Visible to all anonymous and authenticated traffic' },
      { value: 'owner_only', label: 'Internal Only', description: 'Restricted to the catalog owner tenant and seller-driven flows' },
    ],
    statusOptions: [
      { value: 'draft', label: 'Draft' },
      { value: 'active', label: 'Active / Published' }
    ],
    selectorModeOptions: [
      { value: 'all_products', label: 'All Products', description: 'Expose entire catalog automatically' },
      { value: 'explicit_products', label: 'Explicit Products', description: 'Manually select products' },
      { value: 'explicit_variants', label: 'Explicit Variants', description: 'Manually select variants' },
      { value: 'rule_based', label: 'Rule Based', description: 'Filter by tags, categories, or price' }
    ],
    productOptions,
    variantOptions,
    selectorAttributeOptions: [
      { value: 'product_type', label: 'Product Type' },
      { value: 'service_class', label: 'Service Class' },
      { value: 'service_group', label: 'Service Group' },
      { value: 'facet', label: 'Facet' },
      { value: 'flag', label: 'Flag' },
      { value: 'product_id', label: 'Product ID' },
      { value: 'variant_id', label: 'Variant ID' },
    ],
    selectorOperatorOptions: [
      { value: 'equals', label: 'Equals' },
      { value: 'in', label: 'In List' },
      { value: 'contains', label: 'Contains' },
      { value: 'exists', label: 'Exists' },
    ],
    audiencePreview: { reachSummary: 'This publication will be visible to approximately 12,000 storefront users.' },
    fieldHelp: [],
    callouts: bundleWarnings,
    validationErrors: {},
    onChange: (patch) => setForm(curr => ({ ...curr, ...patch })),
    onChangeProductSelection: (productSelection) => setForm(curr => ({ ...curr, productSelection })),
    onSave: handleSave,
    onClose: closeEditor
  };

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Publications' }]}
          title="Publications"
          description="Control storefront reach, audience scope, and default commercial terms for each catalog release."
        />
      }
      padded={false}
      takeoverActive={editorOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={<CatalogPublicationEditorWizard viewModel={editorViewModel} />}
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={publications}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={columnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              if (next.length === 0 && columnFilters.length > 0) return query.clear();
              for (const key of ['scope', 'status']) {
                const filter = next.find(item => item.id === key);
                const values = Array.isArray(filter?.value) ? filter.value.map(String) : [];
                if (values.join('\u0000') !== query.facets[key].join('\u0000')) return query.setFacet(key, values);
              }
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalPublications}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No Publications Configured</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.scope.length > 0 || query.facets.status.length > 0
                    ? 'No publications matched the current search or filters.'
                    : 'Create a publication to connect your catalog to a storefront and define commercial reach.'}
                </DescriptionText>
                {searchQuery || query.facets.scope.length > 0 || query.facets.status.length > 0 ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">Reset search and filters</button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar table={table} searchValue={searchQuery} onSearchChange={query.setSearch} searchPlaceholder="Search all publications..." resultCount={totalPublications} className="border-b border-border/40 bg-transparent px-0 py-0" filters={
                <HorizontalStack gap={2}>
                  <DataTableFacetedFilter title="Status" selectedValues={new Set(query.facets.status)} onFilterChange={values => query.setFacet('status', Array.from(values))} counts={facetCounts.status} options={[{ label: 'Active', value: 'active' }, { label: 'Published', value: 'published' }, { label: 'Draft', value: 'draft' }]} />
                  <DataTableFacetedFilter title="Scope" selectedValues={new Set(query.facets.scope)} onFilterChange={values => query.setFacet('scope', Array.from(values))} counts={facetCounts.scope} options={[{ label: 'Public', value: 'public' }, { label: 'Owner Only', value: 'owner_only' }, { label: 'Shared Tenants', value: 'shared_tenants' }]} />
                </HorizontalStack>
              } />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
